import React, { useContext, useState } from 'react';
import { Spinner } from 'reactstrap';
import { RiCheckboxCircleFill } from 'react-icons/ri';
import { MdTimer, MdOutlinePoll, MdUndo } from 'react-icons/md';
import { Language } from '../App';
import usePollRemainingTime from '../hooks/usePollRemainingTime';

const PollResultsView = ({ value, toggleModal, removePollAnswer }) => {
  const { lang } = useContext(Language);
  const [isUndoing, setIsUndoing] = useState(false);

  const userData = localStorage.getItem("SignupData");
  const user = userData ? JSON.parse(userData) : null;
  const user_id = user?.data?.id;

  const showVoteModal = (id) => {
    if (user_id === value?.user?.id) {
      toggleModal(id);
    }
  };

  const handleUndo = async () => {
    if (isUndoing) return;
    setIsUndoing(true);
    try {
      await removePollAnswer(value?.original_id);
    } catch (err) {
      console.error(err);
    } finally {
      setIsUndoing(false);
    }
  };

  const remainingTime = usePollRemainingTime(value?.time, value?.duration);
  const isClosed = remainingTime === lang?.poll_closed || remainingTime === "Poll closed";
  const optionLetters = ['A', 'B', 'C', 'D'];

  return (
    <div className="px-4 py-2">
      <div className="d-flex flex-column mb-3" style={{ gap: "10px" }}>
        {[value?.option_1, value?.option_2, value?.option_3, value?.option_4]
          .filter((data) => data !== "" && data !== null && data !== undefined)
          .map((data, index) => {
            const isVotedOption = value?.right === index + 1;
            const percentage = Array.isArray(value?.percs) && value.percs[index] !== undefined
              ? value.percs[index]
              : 0;

            return (
              <div
                key={index}
                className="p-3 rounded-3 position-relative overflow-hidden d-flex align-items-center gap-3"
                style={{
                  background: isVotedOption ? "linear-gradient(135deg, #E0F7FA 0%, #E0F2FE 100%)" : "#F8FAFC",
                  border: isVotedOption ? "2px solid #00BADA" : "1.5px solid #E2E8F0",
                  borderRadius: "14px",
                  boxShadow: isVotedOption ? "0px 4px 14px rgba(0, 186, 218, 0.12)" : "none",
                  transition: "all 0.25s ease",
                }}
              >
                {/* Left: Vertically Centered Option Letter Badge */}
                <span
                  className="d-flex align-items-center justify-content-center roboto-bold rounded-circle flex-shrink-0 align-self-center"
                  style={{
                    width: "32px",
                    height: "32px",
                    background: isVotedOption ? "#00BADA" : "#EDF8FA",
                    color: isVotedOption ? "#FFFFFF" : "#00BADA",
                    fontSize: "14px",
                    boxShadow: isVotedOption ? "0px 2px 6px rgba(0, 186, 218, 0.3)" : "none",
                  }}
                >
                  {optionLetters[index] || index + 1}
                </span>

                {/* Right: Text + Percentage Row & Progress Bar */}
                <div className="flex-grow-1 d-flex flex-column gap-1.5" style={{ minWidth: 0 }}>
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                      <span
                        className="roboto-bold fs-14 text-truncate"
                        style={{ color: isVotedOption ? "#0F172A" : "#334155" }}
                      >
                        {data}
                      </span>
                      {isVotedOption && (
                        <span className="d-flex align-items-center flex-shrink-0" style={{ color: "#00BADA" }}>
                          <RiCheckboxCircleFill size={18} />
                        </span>
                      )}
                    </div>
                    <span
                      className="roboto-bold fs-14 flex-shrink-0 ms-2"
                      style={{ color: isVotedOption ? "#00BADA" : "#64748B" }}
                    >
                      {percentage}%
                    </span>
                  </div>

                  {/* Progress Bar */}
                  <div
                    className="w-100 overflow-hidden"
                    style={{
                      height: "8px",
                      borderRadius: "999px",
                      background: isVotedOption ? "#C7F2F9" : "#E2E8F0",
                    }}
                  >
                    <div
                      style={{
                        width: `${percentage}%`,
                        height: "100%",
                        borderRadius: "999px",
                        background: isVotedOption
                          ? "linear-gradient(90deg, #00BADA 0%, #0284C7 100%)"
                          : "linear-gradient(90deg, #CBD5E1 0%, #94A3B8 100%)",
                        boxShadow: isVotedOption ? "0px 0px 8px rgba(0, 186, 218, 0.4)" : "none",
                        transition: "width 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      {/* Bottom Actions Bar */}
      <div className="d-flex align-items-center justify-content-between px-1 py-1">
        <div className="d-flex align-items-center gap-3">
          <div className="d-flex align-items-center gap-2">
            <MdOutlinePoll size={18} style={{ color: "#00BADA" }} />
            <span
              className="mb-0 roboto-bold fs-13 cursor hover-blue"
              onClick={() => showVoteModal(value.id)}
              style={{ color: "#00BADA" }}
            >
              {value.votes_count} {value.votes_count === 1 ? lang.vote || "vote" : lang.votes || "votes"}
            </span>
          </div>

          {!isClosed && value?.user?.id !== user_id && value?.right > 0 && (
            <button
              disabled={isUndoing}
              className="btn btn-sm rounded-pill d-inline-flex align-items-center gap-1 border-0 cursor px-3 py-1"
              style={{
                background: "#FEF2F2",
                color: "#EF4444",
                fontSize: "12px",
                fontWeight: 600,
                transition: "all 0.2s ease",
              }}
              onClick={handleUndo}
            >
              {isUndoing ? (
                <Spinner size="sm" style={{ color: "#EF4444" }} />
              ) : (
                <>
                  <MdUndo size={15} />
                  {lang.undo || "Undo"}
                </>
              )}
            </button>
          )}
        </div>

        {value.duration && (
          <div className="d-flex align-items-center gap-1.5 px-3 py-1 rounded-pill" style={{ background: "#F1F5F9", border: "1px solid #E2E8F0" }}>
            <MdTimer size={14} style={{ color: "#64748B" }} />
            <span className="mb-0 fs-12 roboto-medium" style={{ color: "#64748B" }}>
              {remainingTime}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default PollResultsView;
